import { stringToFormFieldComponent, stringToFormFieldParser } from "components/Form";
import { Form } from "components/Form/components/Form/Form";
import Tooltip from "components/Tooltip/Tooltip";
import { FeRecordType, FeSubrecordType, FormField, RecordId } from "config/config";
import { isWriteOnlyField } from "config/resourceUtils";
import { Fragment } from "react";
import { Field } from "react-final-form";
import "./ResourceEditFields.scss";

interface ResourceEditFieldsProps {
    formStructure?: FormField[];
    isDisabled: boolean;
    isReadOnly: boolean;
    writeOnlyFieldsWithData: any;
    resourceTypeName: FeRecordType | FeSubrecordType;
    recordId: RecordId;
    fetchResourceData?: (() => Promise<any>) | null;
    setNestedFormSaveData?: (value: any) => void;
    nestedFormSaveData?: { [key: string]: () => void };
    defaultValues?: any;
    currentValues?: any;
    // The persisted (backend-returned) record values, i.e. react-final-form's
    // initialValues. Passed as the 2nd arg to getDisabled/fieldTooltip/fieldNote
    // so a field can gate its read-only state on the SAVED value rather than the
    // current unsaved form value (e.g. schedule config is locked while the
    // schedule is persisted-enabled, regardless of the in-session toggle).
    persistedValues?: any;
    additionalFieldProps?: { [key: string]: any };
    enableSaveManually?: (value: boolean) => void;
    isNewRecord?: boolean;
    isLoaded?: boolean;
}

export const ResourceEditFields = ({
    formStructure = [],
    isDisabled,
    isReadOnly,
    writeOnlyFieldsWithData,
    resourceTypeName,
    recordId,
    fetchResourceData = null,
    setNestedFormSaveData,
    nestedFormSaveData,
    defaultValues,
    currentValues,
    persistedValues,
    additionalFieldProps = {},
    enableSaveManually,
    isNewRecord,
    isLoaded,
}: ResourceEditFieldsProps) => (
    <>
        {formStructure.map((field) => {
            const dependee = field.fieldAttr?.dependee;
            const isFieldRendered =
                !dependee ||
                (dependee.fieldValue !== undefined &&
                    currentValues?.[dependee.fieldName] === dependee.fieldValue) ||
                (dependee.isValueSet === true && currentValues?.[dependee.fieldName]) ||
                (dependee.isValueSet === false && !currentValues?.[dependee.fieldName]);

            const isFieldDisabled = field.fieldAttr?.getDisabled?.(
                currentValues,
                persistedValues,
            );

            const { conditionalFieldProps } = field;
            const isConditionMet =
                conditionalFieldProps &&
                currentValues[conditionalFieldProps.condition.fieldName] ===
                    conditionalFieldProps.condition.fieldValue;
            let conditionalReadOnly;
            if (isConditionMet) {
                conditionalReadOnly = conditionalFieldProps.fieldProps.isReadOnly;
                currentValues[field.name] = conditionalFieldProps.fieldProps.value;
            }
            const note = field.fieldProps?.fieldNote?.(currentValues, persistedValues);
            // A write-only field (e.g. a secret/password) must NEVER be seeded
            // with a schema/DB default value: it is empty on load and its stored
            // value is masked, so seeding a default would ship that default in
            // the mutation on any save that leaves the field untouched and
            // silently overwrite the real stored secret. See isWriteOnlyField —
            // keyed off the field component so a new write-only resource cannot
            // forget to register (regression: SECRET.value was clobbered to its
            // `'{}'` server_default on an unrelated-field edit).
            const defaultValue = isWriteOnlyField(field, writeOnlyFieldsWithData)
                ? undefined
                : defaultValues?.[field.name] ??
                  (field.fieldProps?.defaultValue?.value !== undefined
                      ? field.fieldProps?.defaultValue?.value
                      : field.fieldProps?.defaultValue);

            const fieldComponent = (
                <Fragment key={`resource-edit-form-${field.component + field.name}`}>
                    <Form.Row
                        className={`form__row--${field.component}`}
                        key={`resource-edit-form-${field.component + field.name}`}
                    >
                        <Field
                            name={field.name}
                            fieldProps={{
                                ...field.fieldProps,
                                hasValue: writeOnlyFieldsWithData[field.name],
                                resourceTypeName: resourceTypeName,
                                recordId: recordId,
                                enableSaveManually: field.fieldProps.canEnableSaveManually
                                    ? enableSaveManually
                                    : undefined,
                                isConditionMet: field.fieldProps.isConditionMet?.(currentValues),
                                fieldReplacement: field.fieldProps.fieldReplacement,
                                isNewRecord,
                                initialAutocompleteOption:
                                    field.fieldProps.getInitialAutocompleteOption?.(currentValues),
                                isLoaded,
                                defaultValue,
                                valueTransformer: field.fieldProps.valueTransformer
                                    ? (value: unknown) =>
                                          field.fieldProps.valueTransformer(value, currentValues)
                                    : undefined,
                                ...additionalFieldProps,
                            }}
                            fieldAttr={{
                                ...field.fieldAttr,
                                disabled: isDisabled
                                    ? true
                                    : field.fieldProps?.isReadOnly ||
                                      conditionalReadOnly ||
                                      (recordId && field.fieldAttr?.isChangeable === false) ||
                                      isReadOnly ||
                                      isFieldDisabled,
                                hidden:
                                    field.fieldAttr?.hidden ||
                                    (field.fieldProps?.checkAccess &&
                                        additionalFieldProps?.isSSPHidden),
                                fieldTooltip: field.fieldAttr?.fieldTooltip?.(
                                    currentValues,
                                    persistedValues,
                                ),
                            }}
                            component={stringToFormFieldComponent[field.component]}
                            validate={field.validation}
                            parse={
                                stringToFormFieldParser[field.component]
                                    ? stringToFormFieldParser[field.component]
                                    : (value) => value
                            }
                            isEqual={(a, b) => {
                                if (field.isEqual) {
                                    return field.isEqual(a, b);
                                }
                                return a === b;
                            }}
                            fetchResourceData={fetchResourceData}
                            setNestedFormSaveData={setNestedFormSaveData}
                            nestedFormSaveData={nestedFormSaveData}
                            defaultValue={defaultValue}
                        />
                    </Form.Row>
                    {note && !additionalFieldProps?.renderIconOnly ? (
                        <Form.Row key={`resource-edit-form-${field.component + field.name}-note`}>
                            {note}
                        </Form.Row>
                    ) : null}
                </Fragment>
            );

            return isFieldRendered ? (
                additionalFieldProps?.renderIconOnly && field.fieldProps?.iconTooltipOverlay ? (
                    <Tooltip
                        overlay={field.fieldProps.iconTooltipOverlay}
                        key={`resource-edit-form-${field.component + field.name}`}
                    >
                        {fieldComponent}
                    </Tooltip>
                ) : (
                    fieldComponent
                )
            ) : null;
        })}
    </>
);
